// useDivision(): the public division switcher's logic (#583, API.md §5),
// shaped like useSportSwitcher(). Core decides which divisions exist, whether
// the switcher shows, the active pick and what a pick does; each template only
// presents it (Classic: a pill row under the H1; Arena: a sub-bar; Huddle: a
// chip rail).
//
// State lives in localStorage (`mcl_active_division_<host>_<sport>`) behind a
// tiny module store, so every caller (the nav VM, a page controller, a chrome
// data hook) sees the same pick without a provider in the tree. `?division=`
// on a list route wins over the stored pick and is persisted.
//
// A sport with fewer than 2 live divisions is `enabled: false`: no option, no
// stored read, no URL write, `value: null`, and controllers send no param.

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLeague, type TenantDivision } from '@/contexts/LeagueContext'
import { useSportContext } from '@/contexts/SportContext'
import { useLocale } from '@/i18n'
import type { DivisionArg, DivisionRef } from '@/services/api/types'
import { sportSwitcherHiddenOn } from '../chrome/useSportSwitcher'
import {
  ALL_DIVISIONS,
  divisionOptions,
  divisionParamApplies,
  divisionRouteAllowsAll,
  divisionsEnabled,
  isDivisionListRoute,
  liveDivisions,
  parseDivisionParam,
  readStoredDivision,
  resolveDivision,
  writeStoredDivision,
  type DivisionOption,
  type DivisionValue,
} from './divisions'

export interface SiteDivisionSwitcher {
  /** 2+ live divisions in the active sport AND a list route (Home, Schedule, Stats, Teams, Players). */
  visible: boolean
  /** 2+ live divisions in the active sport, whatever the route. False = today's site, exactly. */
  enabled: boolean
  /** All divisions first (where the page allows it), then the live divisions in admin order. [] when not enabled. */
  options: DivisionOption[]
  /** The active pick: a division id, 'all', or null when not enabled. */
  value: DivisionValue | null
  /** The active division; null = All divisions (or not enabled). */
  active: TenantDivision | null
  /** The sport's live divisions in admin order ([] when not enabled). */
  live: TenantDivision[]
  /** Pick: persists for this host and sport and writes `?division=<slug|all>` (replace) on a list route. */
  set: (v: DivisionValue) => void
}

// One version counter for every caller: bumped on a pick or a persisted URL
// pick, so each useDivision() re-reads the stored value.
let version = 0
const listeners = new Set<() => void>()
function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}
const getVersion = () => version
function bump() {
  version += 1
  listeners.forEach((fn) => fn())
}

const DISABLED_OPTIONS: DivisionOption[] = []
const NO_LIVE: TenantDivision[] = []

/**
 * The division switcher for the active sport. `allowAll` defaults to the
 * route's rule (every list page except Stats offers All divisions).
 */
export function useDivision(opts: {
  allowAll?: boolean
  /** Read the stored pick on detail routes too (the Arena scores strip, #723), where the page itself ignores it. */
  storedOnDetail?: boolean
} = {}): SiteDivisionSwitcher {
  const { league } = useLeague()
  const { activeSportId } = useSportContext()
  const location = useLocation()
  const navigate = useNavigate()
  const { pathname, search } = location
  useSyncExternalStore(subscribe, getVersion, getVersion)

  const all = useMemo(() => liveDivisions(league, activeSportId), [league, activeSportId])
  const enabled = divisionsEnabled(all)
  const live = enabled ? all : NO_LIVE
  const allowAll = opts.allowAll ?? divisionRouteAllowsAll(pathname)
  // Detail routes ignore the URL and the stored pick: their data is never filtered.
  const detail = sportSwitcherHiddenOn(pathname)
  const rawParam = enabled && divisionParamApplies(pathname) ? new URLSearchParams(search).get('division') : null
  const url = parseDivisionParam(rawParam, live)
  const stored = enabled && (!detail || opts.storedOnDetail) ? readStoredDivision(activeSportId) : null
  const value = resolveDivision({ live, url, stored, allowAll })
  const active = typeof value === 'number' ? live.find((d) => d.id === value) ?? null : null
  const visible = enabled && isDivisionListRoute(pathname)

  // An inbound `?division=<slug>` wins and is remembered for later visits.
  useEffect(() => {
    if (!enabled || url == null || url === stored) return
    writeStoredDivision(activeSportId, url)
    bump()
  }, [enabled, url, stored, activeSportId])

  // #723: a slug no division answers to (a typo, an archived division) is
  // dropped from the URL, so a copied link doesn't carry it. A live slug of
  // another sport stays: the stored sport may not be restored yet.
  const unknownSlug = rawParam != null && url == null &&
    !(league?.divisions ?? []).some((d) => d.slug === rawParam.trim().toLowerCase())
  useEffect(() => {
    if (!unknownSlug) return
    const params = new URLSearchParams(search)
    params.delete('division')
    const qs = params.toString()
    navigate({ pathname, search: qs ? `?${qs}` : '', hash: location.hash }, { replace: true })
  }, [unknownSlug, search, navigate, pathname, location.hash])

  const set = useCallback((v: DivisionValue) => {
    if (!enabled) return
    writeStoredDivision(activeSportId, v)
    if (visible) {
      const params = new URLSearchParams(search)
      const slug = v === ALL_DIVISIONS ? ALL_DIVISIONS : live.find((d) => d.id === v)?.slug
      if (slug) params.set('division', slug)
      const qs = params.toString()
      navigate({ pathname, search: qs ? `?${qs}` : '', hash: location.hash }, { replace: true })
    }
    bump()
  }, [enabled, activeSportId, visible, search, live, navigate, pathname, location.hash])

  // The "All divisions" option is in the active language.
  const locale = useLocale()
  const options = useMemo(
    () => (enabled ? divisionOptions(live, allowAll) : DISABLED_OPTIONS),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locale is read inside divisionOptions
    [enabled, live, allowAll, locale]
  )
  return { visible, enabled, options, value, active, live, set }
}

/**
 * The `division` request argument for a switcher: undefined when divisions
 * are off (so the request is exactly today's), else the active id or 'all'.
 */
export function divisionArg(sw: Pick<SiteDivisionSwitcher, 'enabled' | 'value'>): DivisionArg | undefined {
  return sw.enabled && sw.value != null ? sw.value : undefined
}

/** A row's division tag: the ref of `id` among the live divisions, or null. */
export function divisionRef(live: readonly TenantDivision[], id: number | null | undefined): DivisionRef | null {
  if (id == null) return null
  const d = live.find((x) => x.id === id)
  return d ? { id: d.id, name: d.name, slug: d.slug, short_name: d.short_name } : null
}

